import React, {FC, useCallback} from 'react';
import {ErrorBoundary} from 'react-error-boundary';

import {buildKnowledgeBasePluginHomeRoute} from '../../../libs/knowledge-base/shared/helpers/pluginHelpers';
import {findPanelSequence} from '../../../libs/shared/core/src/helpers/panels/panelSequence';
import {useInitialEffect} from '../../../libs/shared/core/src/helpers/react/hooks/useEffectHelpers';
import {KnowledgeBaseAccessTypesEnum} from '../../../libs/shared/core/src/models/api/knowledgeBaseModel';
import {getFlatKnowledgeBasesWithPublishedLocale} from '../../../libs/shared/core/src/store/data/knowledgeBases/knowledgeBasesSelectors';
import {getKnowledgeBasePluginPanel} from '../../../libs/shared/core/src/store/panels/knowledgeBasePlugin/knowledgeBasePluginPanelSelectors';
import {
  makeKnowledgeBasePluginPanelId,
  PanelTypesEnum,
  ViewerPanelId,
} from '../../../libs/shared/core/src/store/panels/panelStateBase';
import {useAppDispatch, useAppSelector} from '../../helpers/appReduxHelpers';
import {AppState} from '../../store/appState';
import {
  getPreferredP4KnowledgeBaseId,
  getPreferredP4KnowledgeBaseLocale,
} from '../../store/data/localPreferences/localPreferencesSelectors';
import {removePanelAction, setPanelAction} from '../../store/panels/panelsActions';
import {makeGetAppPanelIdFromPanel} from '../../store/panels/panelsSelectors';
import {SidePanelCloseButton} from '../inboxes/side/sidePanelCloseButton';
import {KnowledgeBasePluginErrorBoundary} from './knowledgeBasePluginErrorBoundary';
import {KnowledgeBasePluginPreferencesContainer} from './knowledgeBasePluginPreferencesContainer';

/*
 * Props.
 */

interface KnowledgeBasePluginContainerProps {
  panelId: ViewerPanelId;
}

/*
 * Component.
 */

export const KnowledgeBasePluginContainer: FC<KnowledgeBasePluginContainerProps> = ({panelId}) => {
  const popupAppPanelId = useAppSelector((state: AppState) => makeGetAppPanelIdFromPanel()(state, {panelId}));
  const existingPanel = useAppSelector(getKnowledgeBasePluginPanel);
  const knowledgeBases = useAppSelector(getFlatKnowledgeBasesWithPublishedLocale);
  const preferredKnowledgeBaseId = useAppSelector(getPreferredP4KnowledgeBaseId);
  const preferredKnowledgeBaseLocale = useAppSelector(getPreferredP4KnowledgeBaseLocale);
  const dispatch = useAppDispatch();

  const createNewPanel = useCallback(
    (shouldRemoveIfExisting?: boolean) => {
      // Check if we already have a plugin panel created.
      if (existingPanel) {
        // If we should remove, we will and regenerate a new one or just do nothing.
        if (shouldRemoveIfExisting) {
          dispatch(removePanelAction(existingPanel.id));
        } else {
          return;
        }
      }
      const preferredKnowledgeBase = knowledgeBases.find((kb) => kb.id === preferredKnowledgeBaseId);
      const knowledgeBasePluginPanelId = makeKnowledgeBasePluginPanelId(panelId);

      // Try to use the preferred knowledge base. If not try to
      // pre-select the first external published knowledge base we find.
      // If we do not find one, we can just use the first knowledge base we do find.
      const selectedKnowledgeBase =
        preferredKnowledgeBase ??
        knowledgeBases.find((kb) => kb.type === KnowledgeBaseAccessTypesEnum.EXTERNAL) ??
        knowledgeBases[0];

      const selectedKnowledgeBaseLocale = (() => {
        // If we do not have the preferred locale, just return the default.
        if (!preferredKnowledgeBaseLocale) {
          return selectedKnowledgeBase?.defaultLocale;
        }
        // If the preferred locale is in the selected knowledge base, return it.
        if (
          preferredKnowledgeBaseLocale &&
          selectedKnowledgeBase?.locales.includes(preferredKnowledgeBaseLocale)
        ) {
          return preferredKnowledgeBaseLocale;
        }
        // If the preferred locale is not in the selected knowledge base, return the default.
        return selectedKnowledgeBase?.defaultLocale;
      })();

      dispatch(
        setPanelAction({
          id: knowledgeBasePluginPanelId,
          type: PanelTypesEnum.KNOWLEDGE_BASE_PLUGIN,
          sequence: findPanelSequence(),
          selectedKnowledgeBaseId: selectedKnowledgeBase?.id,
          selectedKnowledgeBaseLocale,
          categories: {},
          articles: {},
          categoryAsyncStatus: {},
          articlesAsyncStatus: {},
          revisions: {},
          parameters: buildKnowledgeBasePluginHomeRoute(),
        }),
      );
    },
    [
      dispatch,
      existingPanel,
      knowledgeBases,
      panelId,
      preferredKnowledgeBaseId,
      preferredKnowledgeBaseLocale,
    ],
  );

  const onRequestPopOut = useCallback(() => {
    const knowledgeBasePluginPanelId = makeKnowledgeBasePluginPanelId();
    // We would only support popping out if we have an existing panel otherwise there is nothing to do!
    if (!existingPanel) {
      return;
    }
    dispatch(
      setPanelAction({
        // This will maintain the current state of the panel in the new pop-out window. If something should not be
        // copied over to the new panel make sure override it below.
        ...existingPanel,
        id: knowledgeBasePluginPanelId,
        type: PanelTypesEnum.KNOWLEDGE_BASE_PLUGIN,
        sequence: findPanelSequence(),
        popupAppPanelId,
        // TODO: Maintain the current width of the sidepanel.
        popupWidth: 400,
      }),
    );
  }, [dispatch, existingPanel, popupAppPanelId]);

  useInitialEffect(() => {
    createNewPanel();
  });

  return (
    <ErrorBoundary fallbackRender={KnowledgeBasePluginErrorBoundary} onReset={() => createNewPanel(true)}>
      <KnowledgeBasePluginPreferencesContainer
        panelId={existingPanel?.id}
        sidepanelCloseButton={<SidePanelCloseButton />}
        onRequestPopOut={existingPanel ? onRequestPopOut : undefined}
      />
    </ErrorBoundary>
  );
};
